import type { DelayUnit } from '../types/email.types';
import type { FlowEmail } from '@/lib/api/email-flows/types';
import { DELAY_MULTIPLIERS, IMMEDIATE_DELAY_TEXT } from '../constants/emailEditor.constants';

/**
 * Convert delay from seconds to value + unit
 * Chooses the most appropriate unit (days > hours > minutes)
 *
 * @param seconds - Delay in seconds
 * @returns Object with delay value and unit
 */
export function generateDelayFromSeconds(seconds: number): {
    value: number;
    unit: DelayUnit;
} {
    // Convert to days if evenly divisible
    if (seconds % 86400 === 0) {
        return {
            value: seconds / 86400,
            unit: 'days',
        };
    }

    // Convert to hours if evenly divisible
    if (seconds % 3600 === 0) {
        return {
            value: seconds / 3600,
            unit: 'hours',
        };
    }

    // Default to minutes
    return {
        value: seconds / 60,
        unit: 'minutes',
    };
}

/**
 * Convert delay value + unit to seconds
 *
 * @param value - Delay value
 * @param unit - Delay unit (minutes, hours, days)
 * @returns Delay in seconds
 */
export function convertDelayToSeconds(value: number, unit: DelayUnit): number {
    return value * DELAY_MULTIPLIERS[unit];
}

/**
 * Get email delay text for display (e.g., "2 hours", "1 minute")
 * Matches Vue implementation format
 *
 * @param email - The flow email object
 * @returns Formatted delay text
 */
export function getEmailDelayText(email: FlowEmail): string {
    const delay = email.data?.delay || email.delay || 0;
    const { value, unit } = generateDelayFromSeconds(delay);

    if (value === 0) {
        return IMMEDIATE_DELAY_TEXT;
    }

    // Make unit singular if value is 1
    const unitText = value === 1 ? unit.substring(0, unit.length - 1) : unit;

    return `${value} ${unitText}`;
}
